import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Stack,
  Chip,
  Button,
  Paper,
  Skeleton,
} from "@mui/material";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { UserContext } from "../../context/UserProvider";
import MuPb from "../../widgets/MuPb";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AddIcon from "@mui/icons-material/Add";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader, saveToken, toastError } from "../../utils/util";

const AccountsList = () => {
  const { user: userData, loading } = useContext(UserContext);
  const [accountDetails, setAccountDetails] = useState([]);
  const [loggingIn, setLoggingIn] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);

  useEffect(() => {
    const fetchDetails = async () => {
      if (!userData) return;
      if (!userData.accounts || userData.accounts.length === 0) return;

      setDetailsLoading(true);
      try {
        const response = await axios.get(
          `${API_ENDPOINT}/user/get-my-number-details?accounts=${userData.accounts.join(
            ","
          )}`,
          getHeader()
        );
        setAccountDetails(response.data || []);
      } catch (err) {
        console.error("Failed to fetch account details:", err);
      } finally {
        setDetailsLoading(false);
      }
    };

    fetchDetails();
  }, [userData]);

  const handleAccountLogin = useCallback(async (loginId) => {
    try {
      setLoggingIn(true);
      const response = await axios.post(
        `${API_ENDPOINT}/user/associated-account-login`,
        { customerId: loginId },
        getHeader()
      );
      saveToken(response.data.token);
      document.location.href = "/home";
    } catch (error) {
      toastError(error);
    } finally {
      setLoggingIn(false);
    }
  }, []);

  if (loading) {
    return <MuPb />;
  }

  if (!userData) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6" color="textSecondary">
          No user data found
        </Typography>
      </Box>
    );
  }

  // Non-primary account type
  if ((userData.accounts ?? []).length === 0) {
    return (
      <Box sx={{ maxWidth: 480, mx: "auto", p: 3 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: "center",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <Box sx={{ mb: 3 }}>
            <AccountBalanceIcon
              sx={{
                fontSize: 64,
                color: "text.secondary",
                opacity: 0.6,
              }}
            />
          </Box>

          <Typography variant="h6" color="textSecondary" gutterBottom>
            Your account has no additional phone numbers
          </Typography>

          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Add a phone number to enhance your account security and
            accessibility.
          </Typography>

          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => {
              console.log("Add phone number clicked");
            }}
          >
            Add Phone Number
          </Button>
        </Paper>
      </Box>
    );
  }

  // Primary account type with no accounts
  if (!userData.accounts || userData.accounts.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6" color="textSecondary">
          No accounts found for this customer
        </Typography>
      </Box>
    );
  }

  // User type icon
  const getUserTypeIcon = () => {
    switch (userData.userType) {
      case "Admin":
        return <BusinessIcon color="primary" />;
      default:
        return <PersonIcon color="primary" />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header with user info */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          bgcolor: "primary.50",
          border: "1px solid",
          borderColor: "primary.100",
          borderRadius: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2} mb={1}>
          {getUserTypeIcon()}
          <Box>
            <Typography variant="h6" component="div">
              {userData.customerId}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={
                  (userData.accounts ?? []).length > 0 ? "Primary" : "User"
                }
                size="small"
                color="primary"
                variant="filled"
              />
              <Chip
                label={userData.userType}
                size="small"
                color="secondary"
                variant="outlined"
              />
            </Stack>
          </Box>
        </Stack>
      </Paper>

      {/* Accounts list */}
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        Associated Accounts ({userData.accounts.length})
      </Typography>

      <Stack spacing={2}>
        {(detailsLoading ? userData.accounts : accountDetails).map(
          (acc, index) => {
            const account = detailsLoading
              ? {
                  id: acc,
                  display_phone_number: null,
                  quality_rating: null,
                  whatsapp_business_manager_messaging_limit: null,
                  customerId: acc,
                }
              : acc;

            return (
              <Card
                key={account.id || acc}
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  p: 1,
                  "&:hover": {
                    boxShadow: 2,
                    borderColor: "primary.main",
                  },
                }}
              >
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    {/* Account Info */}
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box
                        sx={{
                          p: 1,
                          bgcolor: "primary.50",
                          borderRadius: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <AccountBalanceIcon sx={{ color: "primary.main" }} />
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={500}>
                          {account.customerId || account.id}
                        </Typography>
                        {detailsLoading ? (
                          <Skeleton width={140} height={18} />
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            {account.display_phone_number}
                          </Typography>
                        )}

                        {/* Chips */}
                        <Stack direction="row" spacing={1} mt={0.5}>
                          {detailsLoading ? (
                            <>
                              <Skeleton width={60} height={24} />
                              <Skeleton width={80} height={24} />
                            </>
                          ) : (
                            <>
                              <Chip
                                label={`Quality: ${account.quality_rating}`}
                                size="small"
                                color={
                                  account.quality_rating === "GREEN"
                                    ? "success"
                                    : "warning"
                                }
                              />
                              <Chip
                                label={account.whatsapp_business_manager_messaging_limit.replace(
                                  "TIER_",
                                  "Limit: "
                                )}
                                size="small"
                                variant="outlined"
                              />
                            </>
                          )}
                        </Stack>
                      </Box>
                    </Stack>

                    {/* Action Buttons */}
                    <Stack direction="row" spacing={1} alignItems="center">
                      <IconButton
                        color="primary"
                        onClick={() => {
                          console.log(
                            "View details for account:",
                            account.customerId
                          );
                        }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={loggingIn}
                        onClick={() =>
                          handleAccountLogin(account.customerId || acc)
                        }
                      >
                        {loggingIn ? "Logging in..." : "Login"}
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            );
          }
        )}
      </Stack>
    </Box>
  );
};

export default AccountsList;
